/**
 * Uniform-grid spatial index over static colliders. Cell size = 2 blocks.
 * Query returns candidate collider indices for an AABB; exact tests happen in
 * the sim. Also used by the renderer for camera culling.
 */
export interface Aabb {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

const CELL = 60; // units

export class StaticGrid<T> {
  private cells = new Map<number, T[]>();

  private static key(cx: number, cy: number): number {
    // Pack signed cell coords into one number (levels stay well within ±2^19 cells).
    return (cx + 0x80000) * 0x100000 + (cy + 0x80000);
  }

  insert(item: T, box: Aabb): void {
    const x0 = Math.floor(box.minX / CELL);
    const x1 = Math.floor(box.maxX / CELL);
    const y0 = Math.floor(box.minY / CELL);
    const y1 = Math.floor(box.maxY / CELL);
    for (let cx = x0; cx <= x1; cx++) {
      for (let cy = y0; cy <= y1; cy++) {
        const k = StaticGrid.key(cx, cy);
        let arr = this.cells.get(k);
        if (!arr) {
          arr = [];
          this.cells.set(k, arr);
        }
        arr.push(item);
      }
    }
  }

  /** Collect unique items whose cells intersect the query box. */
  query(box: Aabb, out: T[]): T[] {
    out.length = 0;
    const x0 = Math.floor(box.minX / CELL);
    const x1 = Math.floor(box.maxX / CELL);
    const y0 = Math.floor(box.minY / CELL);
    const y1 = Math.floor(box.maxY / CELL);
    for (let cx = x0; cx <= x1; cx++) {
      for (let cy = y0; cy <= y1; cy++) {
        const arr = this.cells.get(StaticGrid.key(cx, cy));
        if (!arr) continue;
        for (const it of arr) {
          if (!out.includes(it)) out.push(it);
        }
      }
    }
    return out;
  }
}

export function aabbOverlap(a: Aabb, b: Aabb): boolean {
  return a.minX < b.maxX && a.maxX > b.minX && a.minY < b.maxY && a.maxY > b.minY;
}
